/**
 * =============================================================================
 * Página: /afiliado/cadastro/:tenantSlug/:programSlug
 * Versão: 2.3.0 — Suporte seguro a cadastro por convite em programa privado
 *
 * Mudanças v2.1.0 (frontend, sem alterar fluxo/RPC/edge function):
 *   - Validação completa via funções dedicadas (input-validators.ts):
 *     nome, e-mail, telefone (10/11 dígitos BR), CPF/CNPJ (com dígitos
 *     verificadores), senha (8–72 com letra + número).
 *   - Máscaras reforçadas com inputMode + maxLength + autoComplete.
 *   - Sanitização inline de nome (remove < e >) e bloqueio de paste > 500 chars.
 *   - Erro inline por campo (não envia ao backend enquanto inválido).
 *   - Termos do programa renderizados via DOMPurify (sanitizeHtml) para
 *     bloquear XSS caso um admin malicioso injete <script> nos termos.
 *
 * Histórico:
 *   - v2.3.0: envia p_invite_token à RPC pública para carregar programas
 *     invite_only somente quando o link possui convite válido.
 *   - v2.0.0: Landing 2 colunas + máscaras básicas.
 *
 * Roadmap: Construtor de Templates configurável (próxima feature).
 * =============================================================================
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import {
  Loader2,
  UserPlus,
  AlertCircle,
  CheckCircle2,
  TrendingUp,
  Sparkles,
  FileText,
  Mail,
  User,
  Lock,
  Eye,
  EyeOff,
  IdCard,
  Link2,
  Wallet,
  Check,
  type LucideIcon,
} from 'lucide-react';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import {
  useAffiliateBrandingPublic,
  buildBrandingCssVars,
  type AffiliateBrandingLoginBullet,
} from '@/hooks/useAffiliateBranding';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { applyCPFMask, applyCNPJMask } from '@/lib/input-masks';
import {
  validateEmail,
  validatePhone,
  validateCPF,
  validateCNPJ,
  validatePassword,
  validateFullName,
} from '@/lib/input-validators';
import { sanitizeHtml } from '@/lib/sanitize';
// Helper para montar o path de login preservando o tenantSlug — garante
// que o usuário caia na tela de login com o branding personalizado do tenant.
import { affiliateLoginPath, affiliateAwaitingPath } from '@/lib/affiliate-urls';
// v2.2.0 — Termos via Modelo de Contrato
import { ProgramTermsDialog } from '@/components/referral/ProgramTermsDialog';
import type { ProgramTermsData } from '@/hooks/useProgramTermsForAffiliate';
import { cn } from '@/lib/utils';

// Mapa de ícones (string → componente lucide) para bullets do branding.
const ICON_MAP: Record<AffiliateBrandingLoginBullet['icon'], LucideIcon> = {
  link: Link2,
  chart: TrendingUp,
  wallet: Wallet,
  check: Check,
  sparkles: Sparkles,
};

interface ProgramInfo {
  program_id: string;
  tenant_id: string;
  tenant_name: string;
  program_name: string;
  program_slug: string;
  description: string | null;
  require_terms_acceptance: boolean;
  terms_html: string | null;
  auto_approve_affiliates: boolean;
  // v2.2.0 — campos do modelo de contrato vinculado (LEFT JOIN no RPC)
  terms_template_id: string | null;
  terms_template_name: string | null;
  terms_content_html: string | null;
  terms_background_image_path: string | null;
  terms_show_page_numbers: boolean | null;
  terms_page_number_position: string | null;
  terms_bg_margin_top_mm: number | null;
  terms_bg_margin_bottom_mm: number | null;
  terms_pagination_y_offset_mm: number | null;
  terms_cover_pdf_path: string | null;
  terms_base_pdf_path: string | null;
  terms_base_pdf_position: string | null;
}

// Tipo auxiliar para erros por campo
type FieldErrors = Partial<
  Record<
    'full_name' | 'email' | 'phone' | 'password' | 'document_number' | 'terms',
    string
  >
>;

// Limite de paste (defesa contra payload injection)
const MAX_PASTE_LENGTH = 500;

// ---------------------------------------------------------------------------
// Helpers visuais — declarados no top-level do módulo (FORA do componente)
// para evitar que sejam recriados a cada render. Quando estavam dentro de
// AffiliateSignup, cada setState (cada tecla nos inputs) gerava um novo tipo
// de componente, fazendo o React desmontar a subárvore e o input perder foco.
// v2.1.1 — fix de perda de foco nos campos do formulário.
// ---------------------------------------------------------------------------
const Shell: React.FC<{
  children: React.ReactNode;
  brandingStyle: React.CSSProperties;
}> = ({ children, brandingStyle }) => (
  <div
    className="affiliate-shell min-h-[100dvh] grid md:grid-cols-2 bg-[hsl(var(--aff-bg))]"
    style={brandingStyle}
  >
    {children}
  </div>
);

const HeroAside: React.FC<{
  headline: string;
  description?: string | null;
  logoUrl: string | null;
  tenantName: string | null;
  bullets: AffiliateBrandingLoginBullet[];
}> = ({ headline, description, logoUrl, tenantName, bullets }) => (
  <>
    {/* HERO — Desktop */}
    <aside className="aff-hero-surface relative hidden md:flex flex-col justify-between overflow-hidden p-10 lg:p-14">
      <div className="relative z-10 text-[hsl(var(--aff-primary-foreground))]">
        {logoUrl ? (
          <div className="flex flex-col gap-1.5">
            <img
              src={logoUrl}
              alt={tenantName ?? ''}
              className="max-h-14 lg:max-h-16 max-w-[220px] object-contain object-left"
            />
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] opacity-80">
              Painel do Afiliado
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm">
              <Sparkles className="h-5 w-5" />
            </div>
            <span className="text-sm font-semibold tracking-wide opacity-90">
              {tenantName ? `${tenantName} · Painel do Afiliado` : 'Painel do Afiliado'}
            </span>
          </div>
        )}
      </div>

      <div className="relative z-10 max-w-md space-y-7 text-[hsl(var(--aff-primary-foreground))]">
        <div className="space-y-3">
          <p className="text-xs uppercase tracking-[0.2em] opacity-80 font-semibold">
            Programa de indicação
          </p>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
            {headline}
          </h1>
          {description && (
            <p className="text-base lg:text-[17px] leading-relaxed opacity-85">
              {description}
            </p>
          )}
        </div>

        <ul className="space-y-3">
          {bullets.map((b, i) => {
            const Icon = ICON_MAP[b.icon] ?? Sparkles;
            return (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="text-[14.5px] leading-relaxed opacity-95">
                  {b.text}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="relative z-10 flex items-center gap-2 text-[hsl(var(--aff-primary-foreground))] opacity-75">
        <span className="h-1.5 w-1.5 rounded-full bg-white/70" />
        <span className="text-xs font-medium tracking-wide uppercase">
          Cadastro de Afiliado
        </span>
      </div>
    </aside>

    {/* Mini-hero — Mobile */}
    <div className="aff-hero-surface md:hidden relative h-[28dvh] min-h-[200px] flex items-end overflow-hidden px-6 pb-10">
      <div className="relative z-10 text-[hsl(var(--aff-primary-foreground))] space-y-2">
        <div className="flex items-center gap-2">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={tenantName ?? ''}
              className="max-h-10 max-w-[160px] object-contain object-left"
            />
          ) : (
            <>
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
                <Sparkles className="h-4 w-4" />
              </div>
              <span className="text-[12px] font-semibold tracking-wide opacity-90">
                {tenantName ? `${tenantName} · Afiliado` : 'Cadastro'}
              </span>
            </>
          )}
        </div>
        <h1 className="text-[24px] font-bold tracking-tight leading-tight">
          {headline}
        </h1>
      </div>
    </div>
  </>
);

export default function AffiliateSignup() {
  const { tenantSlug, programSlug } = useParams<{
    tenantSlug: string;
    programSlug: string;
  }>();
  const [searchParams] = useSearchParams();
  // Token de convite privado (fluxo invite_only) — vem do AffiliateInviteResolver.
  const inviteToken = searchParams.get('invite');
  const navigate = useNavigate();
  const { signUp, signIn } = useAffiliateAuth();
  const { toast } = useToast();

  const [program, setProgram] = useState<ProgramInfo | null>(null);
  const [loadingProgram, setLoadingProgram] = useState(true);
  const [programError, setProgramError] = useState<string | null>(null);

  // Branding personalizado por tenant — paleta/logo/textos do programa.
  const { data: brandingData } = useAffiliateBrandingPublic({
    tenantSlug: tenantSlug ?? null,
    programSlug: programSlug ?? null,
  });
  const brandingStyle = useMemo(
    () => buildBrandingCssVars(brandingData?.branding),
    [brandingData],
  );
  const logoUrl = brandingData?.branding?.logo_url ?? null;
  const tenantName = brandingData?.tenant_name ?? null;
  const loginCfg = brandingData?.login ?? null;

  // Bullets premium — preferimos o que o admin configurou no branding,
  // senão caímos em uma trinca de benefícios padrão para signup.
  const bullets: AffiliateBrandingLoginBullet[] = useMemo(() => {
    if (loginCfg?.bullets && loginCfg.bullets.length > 0) return loginCfg.bullets;
    return [
      { icon: 'wallet', text: 'Comissões reais a cada indicação convertida' },
      { icon: 'chart',  text: 'Painel próprio com cliques, conversões e ganhos' },
      { icon: 'check',  text: 'Aprovação rápida e segura — você no controle' },
    ];
  }, [loginCfg]);

  // Toggle de visibilidade da senha (padrão premium).
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    phone: '',
    document_number: '',
    document_type: 'cpf' as 'cpf' | 'cnpj',
    terms_accepted: false,
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  // v2.4.0 — e-mail travado quando vem de convite válido (invite_only).
  // Impede edição no input e força reenvio do e-mail original do convite
  // no submit (defesa em profundidade — o backend também recusa divergência).
  const [inviteEmailLocked, setInviteEmailLocked] = useState(false);
  // v2.2.0 — controle do dialog "Ler os termos"
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);

  // v2.2.0 — Monta o shape ProgramTermsData a partir dos campos retornados
  // pela RPC `get_public_program_by_slugs` (já vem com os campos do template
  // via LEFT JOIN — sem nova chamada de RPC). Quando `terms_template_id` é
  // null, retorna null e mantemos o fallback do `terms_html` legado.
  const programTermsPrefetched: ProgramTermsData | null = useMemo(() => {
    if (!program?.terms_template_id) return null;
    const rawPos = (program.terms_base_pdf_position ?? 'before') as string;
    return {
      template_id: program.terms_template_id,
      template_name: program.terms_template_name ?? 'Termos',
      content_html: program.terms_content_html ?? '',
      version: 1,
      background_image_path: program.terms_background_image_path ?? null,
      show_page_numbers: !!program.terms_show_page_numbers,
      page_number_position: program.terms_page_number_position ?? null,
      bg_margin_top_mm: program.terms_bg_margin_top_mm ?? null,
      bg_margin_bottom_mm: program.terms_bg_margin_bottom_mm ?? null,
      pagination_y_offset_mm: program.terms_pagination_y_offset_mm ?? null,
      cover_pdf_path: program.terms_cover_pdf_path ?? null,
      base_pdf_path: program.terms_base_pdf_path ?? null,
      base_pdf_position: rawPos === 'after' ? 'after' : 'before',
    };
  }, [program]);

  // ---------------------------------------------------------------------------
  // Carregar programa
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!tenantSlug || !programSlug) {
      setProgramError('URL inválida');
      setLoadingProgram(false);
      return;
    }

    (async () => {
      const { data, error } = await (supabase.rpc as any)('get_public_program_by_slugs', {
        p_tenant_slug: tenantSlug,
        p_program_slug: programSlug,
        // Convites privados precisam do token para liberar somente o programa
        // nominal do convite. Sem token, a RPC mantém o bloqueio público.
        p_invite_token: inviteToken ?? null,
      });

      if (error) {
        setProgramError('Não foi possível carregar o programa');
      } else if (!data || data.length === 0) {
        setProgramError('Programa não encontrado ou inativo');
      } else {
        setProgram(data[0] as ProgramInfo);
      }
      setLoadingProgram(false);
    })();
  }, [tenantSlug, programSlug, inviteToken]);

  // Pré-preenche o e-mail quando há token de convite (invite_only).
  // Ao mesmo tempo, valida se o convite ainda é válido; se não for, apenas
  // segue o fluxo normal e o backend recusará se o token for exigido.
  useEffect(() => {
    if (!inviteToken) {
      setInviteEmailLocked(false);
      return;
    }
    (async () => {
      const { data } = await supabase.rpc(
        'validate_affiliate_program_invite',
        { p_token: inviteToken } as any,
      );
      const row = (data as any[])?.[0];
      if (row?.valid && row.email) {
        setFormData((prev) => ({ ...prev, email: row.email as string }));
        // v2.4.0 — trava o campo de e-mail: qualquer edição/paste é ignorada
        // e o submit reenvia o e-mail original vinculado ao convite.
        setInviteEmailLocked(true);
      } else {
        setInviteEmailLocked(false);
      }
    })();
  }, [inviteToken]);


  // ---------------------------------------------------------------------------
  // Sanitização: bloqueia paste de payloads gigantes
  // ---------------------------------------------------------------------------
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (text.length > MAX_PASTE_LENGTH) {
      e.preventDefault();
      toast({
        title: 'Conteúdo muito longo',
        description: 'O texto colado excede o limite permitido.',
        variant: 'destructive',
      });
    }
  };

  // ---------------------------------------------------------------------------
  // Termos sanitizados (defesa contra XSS via terms_html)
  // ---------------------------------------------------------------------------
  const termsSafeHtml = useMemo(
    () => sanitizeHtml(program?.terms_html ?? ''),
    [program?.terms_html],
  );

  // ---------------------------------------------------------------------------
  // Validação completa antes do submit
  // ---------------------------------------------------------------------------
  const runValidation = (): FieldErrors => {
    const errs: FieldErrors = {};

    const nameRes = validateFullName(formData.full_name);
    if (!nameRes.isValid) errs.full_name = nameRes.error;

    const emailRes = validateEmail(formData.email);
    if (!emailRes.isValid) errs.email = emailRes.error;
    else if (formData.email.length > 255)
      errs.email = 'E-mail muito longo (máximo 255 caracteres)';

    const passRes = validatePassword(formData.password);
    if (!passRes.isValid) errs.password = passRes.error;

    // Telefone é opcional, mas se preenchido precisa ser válido
    if (formData.phone.trim().length > 0) {
      const phoneRes = validatePhone(formData.phone);
      if (!phoneRes.isValid) errs.phone = phoneRes.error;
    }

    // Documento é opcional, mas se preenchido precisa ser válido
    if (formData.document_number.trim().length > 0) {
      const docRes =
        formData.document_type === 'cpf'
          ? validateCPF(formData.document_number)
          : validateCNPJ(formData.document_number);
      if (!docRes.isValid) errs.document_number = docRes.error;
    }

    if (program?.require_terms_acceptance && !formData.terms_accepted) {
      errs.terms = 'Você precisa aceitar os termos do programa';
    }

    return errs;
  };

  // ---------------------------------------------------------------------------
  // Submit (mantém payload e fluxo originais — apenas envia dígitos puros)
  // ---------------------------------------------------------------------------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!program || !tenantSlug || !programSlug) return;

    setSubmitError(null);

    // Validação completa client-side antes de qualquer chamada ao backend
    const errs = runValidation();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setSubmitError('Corrija os campos destacados antes de continuar.');
      return;
    }

    setSubmitting(true);

    // Normalização: envia dígitos puros para telefone/documento
    const phoneDigits = formData.phone.replace(/\D/g, '');
    const docDigits = formData.document_number.replace(/\D/g, '');

    const result = await signUp({
      tenant_slug: tenantSlug,
      program_slug: programSlug,
      full_name: formData.full_name.trim(),
      email: formData.email.trim().toLowerCase(),
      password: formData.password,
      phone: phoneDigits || undefined,
      document_number: docDigits || undefined,
      document_type: docDigits ? formData.document_type : undefined,
      terms_accepted: formData.terms_accepted,
      invite_token: inviteToken ?? undefined,
    });

    if (!result.success) {
      // Plano v2: HTTP 409 vem com `code` + `redirect_to`
      // - email_already_exists → manda para login + next=/vincular-programa/...
      // - already_member       → manda para login direto
      if (result.code === 'email_already_exists' && result.redirect_to) {
        toast({
          title: 'Você já tem uma conta',
          description:
            result.message ?? 'Faça login para vincular este novo programa.',
        });
        navigate(result.redirect_to, { replace: true });
        return;
      }
      if (result.code === 'already_member' && result.redirect_to) {
        toast({
          title: 'Já cadastrado neste programa',
          description: result.message ?? 'Faça login para acessar.',
        });
        navigate(result.redirect_to, { replace: true });
        return;
      }
      setSubmitError(result.error ?? 'Erro ao cadastrar');
      setSubmitting(false);
      return;
    }

    setSuccess(true);

    // Login automático (e-mail INÉDITO, criado neste signup)
    const loginResult = await signIn(formData.email, formData.password);

    if (loginResult.success && loginResult.can_access) {
      toast({ title: 'Conta criada!', description: 'Bem-vindo!' });
      navigate('/afiliado', { replace: true });
    } else {
      toast({
        title: 'Cadastro recebido',
        description: result.message ?? 'Aguarde aprovação do administrador',
      });
      navigate(affiliateAwaitingPath(tenantSlug), { replace: true });
    }
  };

  // ---------------------------------------------------------------------------
  // Helpers visuais — Shell e HeroAside foram extraídos para o top-level do
  // arquivo (ver acima do componente) para evitar remount em cada keystroke,
  // que causava perda de foco nos <Input>. v2.1.1.
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Render — loading do programa
  // ---------------------------------------------------------------------------
  if (loadingProgram) {
    return (
      <Shell brandingStyle={brandingStyle}>
        <HeroAside
          headline="Junte-se ao programa"
          description="Carregando informações do programa…"
          logoUrl={logoUrl}
          tenantName={tenantName}
          bullets={bullets}
        />
        <main className="flex justify-center px-4 pb-8 md:p-10 lg:p-14 -mt-10 md:mt-0 relative z-10 md:items-center md:justify-center">
          <div className="w-full max-w-md">
            <div className="aff-card rounded-3xl p-6 md:p-8 space-y-4" style={{ boxShadow: 'var(--aff-shadow-hero)' }}>
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-11 w-full rounded-xl" />
              <Skeleton className="h-11 w-full rounded-xl" />
              <Skeleton className="h-11 w-full rounded-xl" />
              <Skeleton className="h-12 w-full rounded-xl" />
            </div>
          </div>
        </main>
      </Shell>
    );
  }

  // ---------------------------------------------------------------------------
  // Render — programa indisponível
  // ---------------------------------------------------------------------------
  if (programError || !program) {
    return (
      <Shell brandingStyle={brandingStyle}>
        <HeroAside
          headline="Programa indisponível"
          description={programError ?? undefined}
          logoUrl={logoUrl}
          tenantName={tenantName}
          bullets={bullets}
        />
        <main className="flex justify-center px-4 pb-8 md:p-10 lg:p-14 -mt-10 md:mt-0 relative z-10 md:items-center md:justify-center">
          <div className="w-full max-w-md">
            <div className="aff-card rounded-3xl p-6 md:p-8 space-y-5 text-center" style={{ boxShadow: 'var(--aff-shadow-hero)' }}>
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[hsl(var(--aff-danger)/0.12)] text-[hsl(var(--aff-danger))]">
                <AlertCircle className="h-8 w-8" />
              </div>
              <div className="space-y-1.5">
                <h2 className="text-[22px] font-bold tracking-tight text-[hsl(var(--aff-foreground))]">
                  Não foi possível carregar
                </h2>
                <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                  {programError ?? 'Programa não encontrado ou inativo.'}
                </p>
              </div>
              <Button asChild variant="outline" className="rounded-xl w-full">
                <Link to={affiliateLoginPath(tenantSlug)}>Ir para login</Link>
              </Button>
            </div>
          </div>
        </main>
      </Shell>
    );
  }

  // ---------------------------------------------------------------------------
  // Render principal — Landing premium em 2 colunas
  // ---------------------------------------------------------------------------
  return (
    <Shell brandingStyle={brandingStyle}>
      <HeroAside
        headline={program.program_name}
        description={program.description}
        logoUrl={logoUrl}
        tenantName={tenantName}
        bullets={bullets}
      />

      {/* COLUNA DO FORMULÁRIO */}
      <main
        className={cn(
          'flex justify-center px-4 pb-8 md:p-10 lg:p-14',
          '-mt-10 md:mt-0 relative z-10',
          'md:items-center md:justify-center',
        )}
      >
        <div className="w-full max-w-md md:max-w-lg">
          <div
            className="aff-card rounded-3xl p-5 md:p-7 space-y-5 aff-animate-in"
            style={{ boxShadow: 'var(--aff-shadow-hero)' }}
          >
            {/* Cabeçalho do card */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-[hsl(var(--aff-primary))]" />
                <h2 className="text-[22px] md:text-[24px] font-bold tracking-tight text-[hsl(var(--aff-foreground))]">
                  Criar conta de afiliado
                </h2>
              </div>
              <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                Preencha seus dados para participar do programa{tenantName ? ` da ${tenantName}` : ''}.
              </p>
            </div>

            {success ? (
              <div className="space-y-5 text-center py-2">
                <div
                  className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl text-[hsl(var(--aff-primary-foreground))]"
                  style={{ background: 'var(--aff-gradient-hero)' }}
                >
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="text-[20px] font-bold tracking-tight text-[hsl(var(--aff-foreground))]">
                    Cadastro concluído
                  </h3>
                  <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                    Estamos te redirecionando para o painel…
                  </p>
                </div>
              </div>
            ) : (
              <>
                {submitError && (
                  <div
                    role="alert"
                    className="flex items-start gap-2 rounded-2xl bg-[hsl(var(--aff-danger)/0.10)] px-3.5 py-2.5 text-[13px] text-[hsl(var(--aff-danger))]"
                  >
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span className="leading-snug">{submitError}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                  {/* Nome completo */}
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="full_name"
                      className="text-[12.5px] font-semibold text-[hsl(var(--aff-foreground))]"
                    >
                      Nome completo *
                    </Label>
                    <div className="relative">
                      <User className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--aff-muted-foreground))]" />
                      <Input
                        id="full_name"
                        value={formData.full_name}
                        onChange={(e) => {
                          // Sanitização inline: remove caracteres usados em injeção
                          const cleaned = e.target.value.replace(/[<>{}]/g, '');
                          setFormData({ ...formData, full_name: cleaned });
                          if (fieldErrors.full_name)
                            setFieldErrors({ ...fieldErrors, full_name: undefined });
                        }}
                        onPaste={handlePaste}
                        required
                        minLength={3}
                        maxLength={120}
                        autoComplete="name"
                        disabled={submitting}
                        aria-invalid={!!fieldErrors.full_name}
                        placeholder="Seu nome completo"
                        className="h-11 pl-10 pr-3 rounded-xl bg-[hsl(var(--aff-surface-2))] border-[hsl(var(--aff-border))] focus-visible:ring-[hsl(var(--aff-primary))] text-[hsl(var(--aff-foreground))]"
                      />
                    </div>
                    {fieldErrors.full_name && (
                      <p className="text-xs text-[hsl(var(--aff-danger))]">{fieldErrors.full_name}</p>
                    )}
                  </div>

                  {/* E-mail + Telefone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="email"
                        className="text-[12.5px] font-semibold text-[hsl(var(--aff-foreground))]"
                      >
                        E-mail *
                      </Label>
                      <div className="relative">
                        {/* v2.4.0 — quando o e-mail vem de convite, mostra
                            cadeado ao invés do envelope para deixar visualmente
                            claro que o campo está bloqueado. */}
                        {inviteEmailLocked ? (
                          <Lock className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--aff-muted-foreground))]" />
                        ) : (
                          <Mail className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--aff-muted-foreground))]" />
                        )}
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => {
                            // v2.4.0 — trava dura no onChange quando o e-mail
                            // é oriundo de convite. Nenhuma edição é aplicada.
                            if (inviteEmailLocked) return;
                            setFormData({ ...formData, email: e.target.value });
                            if (fieldErrors.email)
                              setFieldErrors({ ...fieldErrors, email: undefined });
                          }}
                          onPaste={(e) => {
                            if (inviteEmailLocked) {
                              e.preventDefault();
                              return;
                            }
                            handlePaste(e);
                          }}
                          required
                          maxLength={255}
                          autoComplete="email"
                          inputMode="email"
                          readOnly={inviteEmailLocked}
                          disabled={submitting}
                          aria-invalid={!!fieldErrors.email}
                          placeholder="seu@email.com"
                          className={cn(
                            'h-11 pl-10 pr-3 rounded-xl bg-[hsl(var(--aff-surface-2))] border-[hsl(var(--aff-border))] focus-visible:ring-[hsl(var(--aff-primary))] text-[hsl(var(--aff-foreground))]',
                            inviteEmailLocked &&
                              'cursor-not-allowed opacity-80 select-none',
                          )}
                        />
                      </div>
                      {fieldErrors.email && (
                        <p className="text-xs text-[hsl(var(--aff-danger))]">{fieldErrors.email}</p>
                      )}
                      {inviteEmailLocked && !fieldErrors.email && (
                        <p className="text-[11px] text-[hsl(var(--aff-muted-foreground))] flex items-center gap-1">
                          <Lock className="h-3 w-3" />
                          E-mail vinculado ao convite — não pode ser alterado.
                        </p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="phone"
                        className="text-[12.5px] font-semibold text-[hsl(var(--aff-foreground))]"
                      >
                        Telefone
                      </Label>
                      {/* PhoneInput com seletor de DDI — mantido por consistência. */}
                      <PhoneInput
                        id="phone"
                        value={formData.phone}
                        onChange={(value) => {
                          setFormData({ ...formData, phone: value });
                          if (fieldErrors.phone)
                            setFieldErrors({ ...fieldErrors, phone: undefined });
                        }}
                        placeholder="(11) 99999-9999"
                        disabled={submitting}
                        aria-invalid={!!fieldErrors.phone}
                      />
                      {fieldErrors.phone && (
                        <p className="text-xs text-[hsl(var(--aff-danger))]">{fieldErrors.phone}</p>
                      )}
                    </div>
                  </div>

                  {/* Senha — linha própria para respirar visualmente */}
                  <div className="grid grid-cols-1 gap-4">

                    {/* Senha */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="password"
                        className="text-[12.5px] font-semibold text-[hsl(var(--aff-foreground))]"
                      >
                        Senha *
                      </Label>
                      <div className="relative">
                        <Lock className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--aff-muted-foreground))]" />
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={(e) => {
                            setFormData({ ...formData, password: e.target.value });
                            if (fieldErrors.password)
                              setFieldErrors({ ...fieldErrors, password: undefined });
                          }}
                          onPaste={handlePaste}
                          required
                          minLength={8}
                          maxLength={72}
                          autoComplete="new-password"
                          disabled={submitting}
                          aria-invalid={!!fieldErrors.password}
                          placeholder="Mín. 8 caracteres"
                          className="h-11 pl-10 pr-11 rounded-xl bg-[hsl(var(--aff-surface-2))] border-[hsl(var(--aff-border))] focus-visible:ring-[hsl(var(--aff-primary))] text-[hsl(var(--aff-foreground))]"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                          className="absolute right-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-lg text-[hsl(var(--aff-muted-foreground))] hover:text-[hsl(var(--aff-foreground))] hover:bg-[hsl(var(--aff-surface-2))] transition-colors"
                          tabIndex={-1}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      {fieldErrors.password && (
                        <p className="text-xs text-[hsl(var(--aff-danger))]">{fieldErrors.password}</p>
                      )}
                    </div>

                  </div>

                  {/* Tipo + Documento — linha própria, Tipo estreito (auto) + Documento ocupa o restante */}
                  <div className="grid grid-cols-[7rem_1fr] gap-4">

                      <div className="space-y-1.5">
                        <Label
                          htmlFor="document_type"
                          className="text-[12.5px] font-semibold text-[hsl(var(--aff-foreground))]"
                        >
                          Tipo
                        </Label>
                        <select
                          id="document_type"
                          value={formData.document_type}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              document_type: e.target.value as 'cpf' | 'cnpj',
                              document_number: '',
                            });
                            if (fieldErrors.document_number)
                              setFieldErrors({
                                ...fieldErrors,
                                document_number: undefined,
                              });
                          }}
                          className="w-full h-11 px-3 rounded-xl border border-[hsl(var(--aff-border))] bg-[hsl(var(--aff-surface-2))] text-sm text-[hsl(var(--aff-foreground))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--aff-primary))]"
                          disabled={submitting}
                        >
                          <option value="cpf">CPF</option>
                          <option value="cnpj">CNPJ</option>
                        </select>
                      </div>
                      <div className="space-y-1.5">
                        <Label
                          htmlFor="document_number"
                          className="text-[12.5px] font-semibold text-[hsl(var(--aff-foreground))]"
                        >
                          Documento
                        </Label>
                        <div className="relative">
                          <IdCard className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--aff-muted-foreground))]" />
                          <Input
                            id="document_number"
                            value={formData.document_number}
                            onChange={(e) => {
                              const masked =
                                formData.document_type === 'cpf'
                                  ? applyCPFMask(e.target.value)
                                  : applyCNPJMask(e.target.value);
                              setFormData({
                                ...formData,
                                document_number: masked,
                              });
                              if (fieldErrors.document_number)
                                setFieldErrors({
                                  ...fieldErrors,
                                  document_number: undefined,
                                });
                            }}
                            onPaste={handlePaste}
                            placeholder={
                              formData.document_type === 'cpf'
                                ? '000.000.000-00'
                                : '00.000.000/0000-00'
                            }
                            maxLength={formData.document_type === 'cpf' ? 14 : 18}
                            inputMode="numeric"
                            disabled={submitting}
                            aria-invalid={!!fieldErrors.document_number}
                            className="h-11 pl-10 pr-3 rounded-xl bg-[hsl(var(--aff-surface-2))] border-[hsl(var(--aff-border))] focus-visible:ring-[hsl(var(--aff-primary))] text-[hsl(var(--aff-foreground))]"
                          />
                        </div>
                        {fieldErrors.document_number && (
                          <p className="text-xs text-[hsl(var(--aff-danger))]">{fieldErrors.document_number}</p>
                        )}
                      </div>
                  </div>


                  {/* Termos do Programa (v2.2.0) — Modelo de Contrato vinculado */}
                  {program.require_terms_acceptance && programTermsPrefetched && (
                    <div className="space-y-1.5">
                      <Label className="text-[12.5px] font-semibold text-[hsl(var(--aff-foreground))]">
                        Termos do programa
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full justify-start rounded-xl h-11 border-[hsl(var(--aff-border))] bg-[hsl(var(--aff-surface-2))] hover:bg-[hsl(var(--aff-surface-2))]"
                        onClick={() => setTermsDialogOpen(true)}
                      >
                        <FileText className="h-4 w-4 mr-2 text-[hsl(var(--aff-primary))]" />
                        Ler os termos
                        {programTermsPrefetched.template_name && (
                          <span className="ml-2 text-[hsl(var(--aff-muted-foreground))] truncate">
                            — {programTermsPrefetched.template_name}
                          </span>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Fallback legado: programas antigos com terms_html livre */}
                  {program.require_terms_acceptance &&
                    !programTermsPrefetched &&
                    termsSafeHtml && (
                      <div className="space-y-1.5">
                        <Label className="text-[12.5px] font-semibold text-[hsl(var(--aff-foreground))]">
                          Termos do programa
                        </Label>
                        <ScrollArea className="h-32 w-full rounded-xl border border-[hsl(var(--aff-border))] p-3 bg-[hsl(var(--aff-surface-2))]">
                          <div
                            className="text-xs text-[hsl(var(--aff-muted-foreground))] prose prose-sm max-w-none"
                            // Conteúdo já passou por sanitizeHtml (DOMPurify)
                            dangerouslySetInnerHTML={{ __html: termsSafeHtml }}
                          />
                        </ScrollArea>
                      </div>
                    )}

                  {program.require_terms_acceptance && (
                    <div className="space-y-1">
                      <div className="flex items-start gap-2">
                        <Checkbox
                          id="terms"
                          checked={formData.terms_accepted}
                          onCheckedChange={(checked) => {
                            setFormData({
                              ...formData,
                              terms_accepted: checked === true,
                            });
                            if (fieldErrors.terms)
                              setFieldErrors({ ...fieldErrors, terms: undefined });
                          }}
                          disabled={submitting}
                        />
                        <Label
                          htmlFor="terms"
                          className="text-sm font-normal cursor-pointer text-[hsl(var(--aff-foreground))]"
                        >
                          Li e aceito os termos do programa de indicação
                        </Label>
                      </div>
                      {fieldErrors.terms && (
                        <p className="text-xs text-[hsl(var(--aff-danger))]">{fieldErrors.terms}</p>
                      )}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 rounded-xl text-[hsl(var(--aff-primary-foreground))] hover:opacity-90 border-0 font-semibold text-[15px] shadow-[var(--aff-shadow-soft)] transition-opacity"
                    style={{ background: 'var(--aff-gradient-hero)' }}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Cadastrando...
                      </>
                    ) : (
                      'Criar conta de afiliado'
                    )}
                  </Button>

                  <p className="text-center text-xs text-[hsl(var(--aff-muted-foreground))]">
                    {program.auto_approve_affiliates
                      ? 'Sua conta será ativada automaticamente.'
                      : 'Sua conta passará por aprovação do administrador.'}
                  </p>
                </form>
              </>
            )}

            <div className="border-t border-[hsl(var(--aff-border))] pt-4 text-center">
              <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                Já tem conta?{' '}
                <Link
                  to={affiliateLoginPath(tenantSlug)}
                  className="font-semibold text-[hsl(var(--aff-primary))] hover:underline underline-offset-2"
                >
                  Fazer login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* v2.2.0 — Dialog dos Termos do Programa (Modelo de Contrato) */}
      <ProgramTermsDialog
        open={termsDialogOpen}
        onOpenChange={setTermsDialogOpen}
        prefetched={programTermsPrefetched}
        programName={program?.program_name ?? null}
      />
    </Shell>
  );
}
